/**
 * Resolves the true innermost target of an event, looking through open shadow roots.
 *
 * A listener outside a shadow tree (e.g. an outside-click handler on `document`) sees
 * `event.target` retargeted to the shadow *host* element, so
 * `element.contains(event.target)` fails for every click inside the shadow tree —
 * the Chrome extension's panel reads as "outside" and closes its own popovers.
 * `composedPath()[0]` is the un-retargeted target for open shadow roots.
 */
export const getComposedEventTarget = (event: Event): Node | null => {
	const path = event.composedPath();

	return (path.length > 0 ? path[0] : event.target) as Node | null;
};

/**
 * Containment check for an outside-click handler: did `event` happen inside `node`?
 *
 * Use this — never `node.contains(getComposedEventTarget(event))` — whenever the question is
 * "is the click inside this panel/menu/popover?". `getComposedEventTarget` returns
 * `composedPath()[0]`, the real innermost target, which for a press on content rendered inside
 * an OPEN shadow root (an `IsolatedHtml` message body in a lead panel) sits *inside* that shadow
 * tree. `Node.contains` does NOT descend into shadow roots, so `node.contains(thatTarget)` is
 * false even though the click was plainly inside `node` — the handler reads it as "outside" and
 * closes the panel, so the user cannot select or copy a reply (PREC-4449).
 *
 * `composedPath()` flattens every open shadow boundary between the target and the window, so
 * `includes(node)` is true for any event that truly originated inside `node` and false otherwise
 * — the same answer as `contains` for a light-DOM click, the correct answer for a shadow one.
 * It is the web twin of the extension combobox's `e.composedPath().includes(containerRef.current)`.
 *
 * Returns false for a null/undefined node, so existing `ref.current && …` guards keep their
 * meaning when the ref is unset.
 */
export const composedPathContains = (node: Node | null | undefined, event: Event): boolean =>
	node != null && event.composedPath().includes(node);
